import { newRelic } from '~/src/clients/newRelic/NewRelicClient';
import { environment } from '~/src/utils/environment';

// Matches the action name Feron reports, so a single query covers both frontends.
// The New Relic browser app differs per frontend, so `appName` separates them.
const CSP_VIOLATION_ACTION = 'CSPViolation';

// report type to observe
const CSP_REPORT_TYPE = 'csp-violation';

// The DOM lib types `ReportBody` as `{ toJSON() }` only, so the CSP fields are declared here.
type CSPViolation = {
  blockedURL?: string;
  columnNumber?: number;
  disposition?: string;
  documentURL?: string;
  effectiveDirective?: string;
  lineNumber?: number;
  sourceFile?: string;
  statusCode?: number;
};

// Query strings and fragments can carry tokens and merchant data, so only the
// path is reported. `sample` and `referrer` are dropped for the same reason.
const stripSensitiveParams = (value: string) => value.split(/[?#]/)[0];

const reportViolation = (body: CSPViolation) => {
  newRelic.addPageAction(CSP_VIOLATION_ACTION, {
    blockedURL: stripSensitiveParams(body.blockedURL || ''),
    columnNumber: body.columnNumber || 0,
    disposition: body.disposition || '',
    documentURL: stripSensitiveParams(body.documentURL || ''),
    effectiveDirective: body.effectiveDirective || '',
    lineNumber: body.lineNumber || 0,
    sourceFile: stripSensitiveParams(body.sourceFile || ''),
    statusCode: body.statusCode || 0,
    timestamp: new Date().toISOString(),
    url: stripSensitiveParams(window.location.href),
    userAgent: navigator.userAgent,
  });
};

const setupLegacyListener = () => {
  document.addEventListener('securitypolicyviolation', (event) => {
    reportViolation({
      blockedURL: event.blockedURI,
      columnNumber: event.columnNumber,
      disposition: event.disposition,
      documentURL: event.documentURI,
      effectiveDirective: event.effectiveDirective,
      lineNumber: event.lineNumber,
      sourceFile: event.sourceFile,
      statusCode: event.statusCode,
    });
  });
};

export const setupCSPReporter = () => {
  if (environment.NEW_RELIC_ENABLED === 'false') return;

  // fallback for browsers that do not support ReportingObserver API
  if (typeof ReportingObserver === 'undefined') {
    setupLegacyListener();
    return;
  }

  // `buffered true` option is to observe reports before the creation of the observer
  const observer = new ReportingObserver(
    (reports) => {
      reports.forEach((report) => {
        if (report.type === CSP_REPORT_TYPE && report.body) {
          reportViolation(report.body as CSPViolation);
        }
      });
    },
    { buffered: true, types: [CSP_REPORT_TYPE] },
  );

  observer.observe();
};
